import React, {useRef, useContext, useCallback} from 'react';
import PropTypes from 'prop-types';
import FormFieldWrapper from 'mrf/view/FormFieldWrapper';
import {StyledFileContainer, StyledFileLabel} from 'styles/components/FileField.styles';
import {FormContext} from 'mrf/view/FormCRUD';
import NonIdFieldPropType from 'mrf/utils/NonIdFieldPropType';
import {generateHtmlId} from 'mrf/utils/StringGenerators';
import {useFormFullSelector} from 'mrf/redux/ReduxHelper';
import T from 'mrf/utils/T';
import Hidden from 'styles/basic/Hidden';

UploadField.propTypes = {
    context: PropTypes.object, label: PropTypes.string, field: NonIdFieldPropType, // Field name.
    placeholder: PropTypes.string, icon: PropTypes.elementType, htmlId: PropTypes.string,
};

export const asyncFileReader = file => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve({
            name: file.name, blob: reader.result,
        });

        reader.onerror = reject;

        reader.readAsDataURL(file);
    });
}

export default function UploadField({context, field, placeholder, icon: Icon = 'div', htmlId, label, ...props}) {
    const formContext = useContext(FormContext);
    if (!context) {
        context = formContext;
    }

    const refDrop = useRef(null);

    const {set} = useFormFullSelector(context)
    
    htmlId = htmlId ?? generateHtmlId(context.resource ?? 'FormField', field);
    
    const handleChange = async e => {
        e.preventDefault?.();
        e.stopPropagation?.();
        const file = await asyncFileReader(e.target.files[0]);

        set({
            [field]: {
                name: file.name, blob: file.blob,
            },
        });
    }

    const FileWrapper = useCallback(() => <>
        <StyledFileContainer htmlFor={htmlId} ref={refDrop} {...props}>
            <Icon size='25'/>
            <StyledFileLabel>
                <T>{placeholder}</T>
            </StyledFileLabel>
        </StyledFileContainer>
    </>, [htmlId, placeholder, props]);

    return (<>
        <Hidden
            as='input'
            type='file'
            onChange={handleChange}
            id={htmlId}
        />
        <FormFieldWrapper
            context={context}
            label={label}
            field={field}
            alwaysUndefinedValue={true}
        >
            <FileWrapper/>
        </FormFieldWrapper>
    </>);
}
